import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RoleBasedRoute } from './components/RoleBasedRoute';

// Eager imports (páginas principais usadas com frequência)
import { LoginPage } from '../components/LoginPage';
import { MainMenu } from '../components/MainMenu';
import { HistoricoPage } from '../components/Historico/HistoricoPage';
import { PedidosExportLayout } from './layouts/PedidosExportLayout';
// PedidosPage e ExportarPage são importados diretamente no PedidosExportLayout

// Lazy imports (rotas pesadas - carregadas sob demanda)
const AdminPage = lazy(() => import('../components/AdminPage').then(m => ({ default: m.AdminPage })));
const PermissionsPage = lazy(() => import('../components/PermissionsPage').then(m => ({ default: m.PermissionsPage })));
// LEGADO: Dashboard analítico completo (4 abas, cache manual, gráficos SVG)
const DashboardLegacyPage = lazy(() => import('../components/Dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })));
// NOVO: Dashboard focado em produção (TanStack Query, Recharts, 6 cards)
const DashboardProducaoPage = lazy(() => import('../components/Dashboard/Producao/DashboardProducaoPage').then(m => ({ default: m.DashboardProducaoPage })));
const DashboardKpisPage = lazy(() => import('../components/Dashboard/Kpis/DashboardKpisPage').then(m => ({ default: m.DashboardKpisPage })));
const MessagesManagementPage = lazy(() => import('../components/pages/MessagesManagementPage').then(m => ({ default: m.MessagesManagementPage })));
const ShortcutsManagementPage = lazy(() => import('../components/pages/ShortcutsManagementPage').then(m => ({ default: m.ShortcutsManagementPage })));
const CampanhasListPage = lazy(() => import('../components/pages/CampanhasListPage').then(m => ({ default: m.CampanhasListPage })));
const CampanhasAdminPage = lazy(() => import('../components/pages/CampanhasAdminPage').then(m => ({ default: m.CampanhasAdminPage })));
const CampanhaDetalhesPage = lazy(() => import('../components/pages/CampanhaDetalhesPage').then(m => ({ default: m.CampanhaDetalhesPage })));
const QuizPlayer = lazy(() => import('../components/Quiz/QuizPlayer').then(m => ({ default: m.QuizPlayer })));
const CampanhaResultadoPage = lazy(() => import('../components/pages/CampanhaResultadoPage').then(m => ({ default: m.CampanhaResultadoPage })));
const CampanhaRankingPage = lazy(() => import('../components/pages/CampanhaRankingPage').then(m => ({ default: m.CampanhaRankingPage })));
// NOVO: Painel administrativo refatorado (migração incremental do menu admin)
const AdminHubPage = lazy(() => import('../components/AdminHub/AdminHubPage').then(m => ({ default: m.AdminHubPage })));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center h-screen bg-navy-900">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
      <p className="text-gray-400 text-sm">Carregando...</p>
    </div>
  </div>
);

interface AppRoutesProps {
  user: {
    email: string;
    nome: string;
    tipo: string;
  } | null;
  embeddedEntry: React.ReactNode;
  onLogout: () => void;
  onOpenModal: (title: string, content: React.ReactNode, size?: 'sm' | 'md' | 'lg') => void;
  onNewOrder: () => void;
}

export function AppRoutes({ user, embeddedEntry, onLogout, onOpenModal, onNewOrder }: AppRoutesProps) {
  // Helper para envolver rotas lazy com Suspense
  const withSuspense = (Component: React.LazyExoticComponent<any>) => (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/embedded" element={<>{embeddedEntry}</>} />
      <Route path="/login" element={<LoginPage />} />

      {/* Redirect root to login */}
      <Route path="/" element={<Navigate to="/login" replace />} />

      {/* Protected Routes */}
      <Route
        path="/main"
        element={
          <ProtectedRoute>
            <MainMenu
              user={user!}
              onLogout={onLogout}
              onNavigate={() => {}}
              onOpenModal={onOpenModal}
              onNewOrder={onNewOrder}
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="/historico"
        element={
          <ProtectedRoute>
            <HistoricoPage />
          </ProtectedRoute>
        }
      />

      {/* NOVO: Painel administrativo refatorado (migração incremental) */}
      <Route
        path="/painel"
        element={
          <ProtectedRoute>
            <RoleBasedRoute allowedRoles={['admin']}>
              {withSuspense(AdminHubPage)}
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />

      {/* Admin Routes - Protected + Role-based (Lazy Loaded) */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <RoleBasedRoute allowedRoles={['admin']}>
              {withSuspense(AdminPage)}
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/permissions"
        element={
          <ProtectedRoute>
            <RoleBasedRoute allowedRoles={['admin']}>
              {withSuspense(PermissionsPage)}
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />

      {/* Dashboard Produção — NOVO (refatorado com TanStack Query + Recharts) */}
      <Route
        path="/dashboard/producao"
        element={
          <ProtectedRoute>
            {withSuspense(DashboardProducaoPage)}
          </ProtectedRoute>
        }
      />

      {/* Dashboard Analítico — LEGADO (manter acessível durante transição) */}
      <Route
        path="/dashboard/legacy"
        element={
          <ProtectedRoute>
            {withSuspense(DashboardLegacyPage)}
          </ProtectedRoute>
        }
      />

      {/* KPIs de Qualidade */}
      <Route
        path="/dashboard/kpis"
        element={
          <ProtectedRoute>
            {withSuspense(DashboardKpisPage)}
          </ProtectedRoute>
        }
      />

      {/* Redirect /dashboard → /dashboard/producao (novo é o padrão) */}
      <Route path="/dashboard" element={<Navigate to="/dashboard/producao" replace />} />

      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            {withSuspense(MessagesManagementPage)}
          </ProtectedRoute>
        }
      />

      <Route
        path="/shortcuts"
        element={
          <ProtectedRoute>
            <RoleBasedRoute allowedRoles={['admin']}>
              {withSuspense(ShortcutsManagementPage)}
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />

      {/* Campanhas Routes - Available to all authenticated users (Lazy Loaded) */}
      <Route
        path="/campanhas"
        element={
          <ProtectedRoute>
            {withSuspense(CampanhasListPage)}
          </ProtectedRoute>
        }
      />

      <Route
        path="/campanhas/:id"
        element={
          <ProtectedRoute>
            {withSuspense(CampanhaDetalhesPage)}
          </ProtectedRoute>
        }
      />

      <Route
        path="/campanhas/:id/jogar"
        element={
          <ProtectedRoute>
            {withSuspense(QuizPlayer)}
          </ProtectedRoute>
        }
      />

      <Route
        path="/campanhas/:id/resultado"
        element={
          <ProtectedRoute>
            {withSuspense(CampanhaResultadoPage)}
          </ProtectedRoute>
        }
      />

      <Route
        path="/campanhas/:id/ranking"
        element={
          <ProtectedRoute>
            {withSuspense(CampanhaRankingPage)}
          </ProtectedRoute>
        }
      />

      {/* Campanhas Admin - Only for admins (Lazy Loaded) */}
      <Route
        path="/campanhas-admin"
        element={
          <ProtectedRoute>
            <RoleBasedRoute allowedRoles={['admin']}>
              {withSuspense(CampanhasAdminPage)}
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />

      {/* Pedidos + Export - Layout compartilhado para preservar state */}
      {/* ✅ CORREÇÃO: Rotas separadas apontando para o mesmo layout */}
      {/* O layout renderiza PedidosPage e ExportarPage internamente (sem Outlet) */}
      {/* Isso garante que PedidosPage (e OrderDetailsWindow) NUNCA desmonta ao navegar */}
      <Route
        path="/pedidos"
        element={
          <ProtectedRoute>
            <PedidosExportLayout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/exportar"
        element={
          <ProtectedRoute>
            <PedidosExportLayout />
          </ProtectedRoute>
        }
      />

      {/* Fallback: páginas não migradas ainda renderizam vazio */}
      <Route path="*" element={<div />} />
    </Routes>
  );
}
